import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import type { Message } from '@/types/message';
import type { DiceGroup, DiceType, DiceRollResult } from '@/types/dice';
import { diceService } from '@/services/diceService';
import { apiService } from '@/services/api';
import { useMessageHistory } from './useMessageHistory';

// Importar a mesma URL base usada no serviço de API
import { API_BASE_URL as BASE_URL } from '@/services/api';
// URL base para API
const API_BASE_URL = BASE_URL;

// URL base para WebSocket (convertendo http para ws ou https para wss)
// Detectar protocolo baseado na URL atual
const WS_PROTOCOL = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
const WS_HOST = window.location.hostname;
const WS_BASE_URL = `${WS_PROTOCOL}//${WS_HOST}`;

interface UseGameWebSocketProps {
  tableId: string;
  userId?: string;
  token?: string;
  onNewMessage?: (message: Message) => void;
  /** A rolagem virou registro no chat: a tela unica pode sair. Leva o dono
   *  da rolagem para so fechar a tela do momento certo. */
  onDiceRollPublished?: (data?: { user_id?: string }) => void;
  /** O mestre pediu para repetir a jogada (fase B): ela sai de cena e a
   *  bandeja de quem rolou volta a montagem. A tela NAO fecha. */
  onDiceRollRepeated?: (data: {
    table_id: string;
    roll_id: string;
    user_id: string;
    repeated_by: string;
  }) => void;
  /** Espelho da montagem (fase A): quem monta toca, a mesa toda ve. */
  onDiceTrayUpdate?: (data: {
    session_id: string;
    user_id: string;
    dice: string[];
    modifiers: { id: string; name: string; value: number }[];
  }) => void;
  onDiceRollStart?: (data: {
    user_id: string;
    user_name: string;
    dice_type: string;
    character_name?: string;
    start_time: string;
  }) => void;
  onDiceRollEnd?: (data: {
    user_id: string;
    user_name: string;
    dice_type: string;
    result: number;
  }) => void;
  onDiceModalOpen?: (data: {
    user_id: string;
    user_name: string;
    dice_type: string;
    character_id?: string;
    character_name?: string;
    session_id: string;
    open_time: string;
  }) => void;
  onDiceModalClose?: (data: {
    session_id: string;
    reason: string;
  }) => void;
  onLiveSyncSync?: (data: any) => void;
  onPollSync?: (data: any) => void;
  onLabSync?: (data: any) => void;
  onMemberJoined?: (member: any) => void;
  onCharacterCreated?: (character: any) => void;
  onCharacterStatsUpdate?: (data: {
    character_id: string;
    character_name?: string;
    updates: {
      hp_current?: number;
      hp_total?: number;
      points?: number;
      level?: number;
      attributes?: Record<string, number>;
    };
  }) => void;
}

interface UseGameWebSocketReturn {
  messages: Message[];
  isConnected: boolean;
  isReconnecting: boolean;
  connectionError: string | null;
  sendMessage: (content: string, messageType: string, characterId?: string, mediaFileId?: string, mediaFile?: { id: string; url: string; filename: string; type: string; size: number }, characterName?: string) => boolean;
  updateMessage: (messageId: string, content: string) => Promise<void>; // Função para atualizar mensagem otimisticamente
  rollDice: (
    diceType: DiceType,
    characterId?: string,
    sessionId?: string,
    attributeId?: string,
    dice?: DiceGroup[],
    attributeIds?: string[]
  ) => Promise<DiceRollResult | null>;
  reconnect: () => void; // Nova função para reconectar manualmente
  loadingHistory: boolean; // Novo estado para carregamento histórico
  loadMoreMessages: () => Promise<void>; // Carregar mensagens mais antigas
  reloadHistory: (missionId?: string) => Promise<void>; // Recarregar filtrando por missão
  loadingMoreMessages: boolean; // Estado de carregamento de mais mensagens
  hasMoreMessages: boolean; // Se há mais mensagens para carregar
  websocket: WebSocket | null; // Expor WebSocket para outros hooks (envio)
  subscribe: (fn: (data: any) => void) => () => void; // Hub: inscrever handler nas mensagens já parseadas
}

/**
 * Hook para gerenciar a conexão WebSocket de uma mesa de jogo
 */
export function useGameWebSocket({
  tableId,
  userId,
  token,
  onNewMessage,
  onDiceRollPublished,
  onDiceRollStart,
  onDiceRollEnd,
  onDiceModalOpen,
  onDiceModalClose,
  onDiceTrayUpdate,
  onDiceRollRepeated,
  onLiveSyncSync,
  onPollSync,
  onLabSync,
  onMemberJoined,
  onCharacterCreated,
  onCharacterStatsUpdate
}: UseGameWebSocketProps): UseGameWebSocketReturn {
  // Histórico/paginação vivem em hook próprio; o useGameWebSocket só aplica
  // os deltas de tempo real via setMessages.
  const {
    messages,
    setMessages,
    loadingHistory,
    loadingMoreMessages,
    hasMoreMessages,
    loadMessageHistory,
    loadMoreMessages,
    reloadHistory,
    getMissionFilter,
  } = useMessageHistory({ tableId, token });

  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const [websocket, setWebsocket] = useState<WebSocket | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<number | null>(null);
  const reconnectAttempts = useRef(0);

  // Callbacks acessados via ref: o socket.onmessage é definido uma única vez
  // dentro de connect() e viveria com as versões dos callbacks congeladas
  // naquele render. Roteando por este ref (atualizado a cada render), o
  // handler sempre chama a versão mais recente — remove a fragilidade que
  // ja obrigou workarounds como o currentUserIdRef no useDiceControls.
  const callbacksRef = useRef({
    onNewMessage, onDiceRollPublished, onDiceRollStart, onDiceRollEnd, onDiceModalOpen, onDiceModalClose,
    onDiceTrayUpdate, onDiceRollRepeated, onLiveSyncSync, onPollSync, onLabSync, onMemberJoined,
    onCharacterCreated, onCharacterStatsUpdate,
  });
  callbacksRef.current = {
    onNewMessage, onDiceRollPublished, onDiceRollStart, onDiceRollEnd, onDiceModalOpen, onDiceModalClose,
    onDiceTrayUpdate, onDiceRollRepeated, onLiveSyncSync, onPollSync, onLabSync, onMemberJoined,
    onCharacterCreated, onCharacterStatsUpdate,
  };

  // Hub de assinatura: este hook é o ÚNICO dono do socket. Cada frame é
  // parseado UMA vez no onmessage e o objeto já parseado é entregue aos
  // inscritos. usePresence/usePrivateChat/useTurnControl usam subscribe()
  // em vez de addEventListener('message') próprio (que reparseava tudo).
  const subscribersRef = useRef(new Set<(data: any) => void>());
  const subscribe = useCallback((fn: (data: any) => void) => {
    subscribersRef.current.add(fn);
    return () => {
      subscribersRef.current.delete(fn);
    };
  }, []);

  // Dispatch das mensagens já parseadas. Antes era um if-chain de ~280 linhas
  // dentro de connect().onmessage; extraído para cá como um switch (tabela
  // type -> handler) + fallback para mensagem de chat comum. setMessages e
  // callbacksRef são estáveis, então deps [] é correto.
  const handleParsedMessage = useCallback((data: any) => {
    const dev = import.meta.env.DEV;
    const cb = callbacksRef.current;

    switch (data.type) {
      case 'presence_update':
        // Já tratado pelos inscritos (usePresence) — nada a fazer aqui.
        return;

      case 'dice_roll': {
        if (dev) console.log('🎲 Dados rolados via WebSocket:', data.data);
        const rawData = data.data;
        const diceResult: DiceRollResult = {
          ...rawData,
          created_at: rawData.timestamp || rawData.created_at || new Date().toISOString()
        };
        // Usar message_id se disponível (para poder deletar/editar), senão dice_roll.id
        const messageId = (rawData.message_id || diceResult.id) as string;
        const diceMessage: Message = {
          id: messageId,
          content: diceService.formatResult(diceResult),
          messageType: 'dice-roll',
          userId: diceResult.user_id,
          userFullName: diceResult.user_name,
          characterId: diceResult.character_id,
          characterName: diceResult.character_name,
          timestamp: diceResult.created_at,
          isPrivate: false,
          tableId: diceResult.table_id,
          diceType: diceResult.dice_type,
          diceResult: diceResult.result,
          // A jogada leva a conta inteira: os dados, as parcelas da ficha e o
          // total. Um dado so tambem entra aqui quando houve parcela — senao o
          // balao mostraria o dado cru e a soma sem o atributo (bug relatado
          // em uso: "nao ta somando com a pontuacao do atributo").
          ...(diceResult.dice_spec || diceResult.modifiers?.length ? {
            dicePool: {
              spec: diceResult.dice_spec
                ?? [{ type: diceResult.dice_type, result: diceResult.result }],
              kept: diceResult.kept ?? [0],
              rule: diceResult.rule,
              expression: diceResult.expression,
              modifiers: diceResult.modifiers,
              total: diceResult.total
            }
          } : {}),
          ...(diceResult.attribute_name ? {
            diceTest: {
              attributeName: diceResult.attribute_name,
              attributeValue: diceResult.attribute_value ?? 0,
              bonus: diceResult.bonus ?? 0,
              total: diceResult.total ?? diceResult.result,
              difficulty: diceResult.difficulty,
              outcome: diceResult.outcome,
              advantage: diceResult.advantage,
              droppedResult: diceResult.dropped_result,
              critical: diceResult.critical,
              actionName: diceResult.action_name
            }
          } : {})
        };
        setMessages((prev) => {
          if (prev.some(msg => msg.id === messageId)) {
            if (dev) console.log('[WebSocket] Mensagem de dados já existe por ID:', messageId);
            return prev;
          }
          return [...prev, diceMessage];
        });
        cb.onNewMessage?.(diceMessage);
        // O momento acabou porque virou registro — a tela unica sai daqui.
        cb.onDiceRollPublished?.({ user_id: diceResult.user_id });
        return;
      }

      case 'dice_roll_start':
        if (dev) console.log('🎲 Início de rolagem compartilhada:', data.data);
        cb.onDiceRollStart?.(data.data);
        return;

      case 'dice_roll_end':
        if (dev) console.log('🎲 Fim de rolagem compartilhada:', data.data);
        cb.onDiceRollEnd?.(data.data);
        return;

      case 'dice_modal_open':
        if (dev) console.log('🎲 Modal de dados aberto compartilhado:', data.data);
        cb.onDiceModalOpen?.(data.data);
        return;

      case 'dice_modal_close':
        if (dev) console.log('🎲 Modal de dados fechado compartilhado:', data.data);
        cb.onDiceModalClose?.(data.data);
        return;

      case 'dice_roll_repeated':
        // O mestre pediu para repetir: a jogada sai de cena e a montagem volta.
        if (dev) console.log('🎲 Jogada repetida a pedido do mestre:', data.data);
        cb.onDiceRollRepeated?.(data.data);
        return;

      case 'dice_tray_updated':
        // Espelho da montagem (fase A): a bandeja de quem monta, para todos.
        cb.onDiceTrayUpdate?.(data.data);
        return;

      case 'livesync_sync':
        if (dev) console.log('[LIVESYNC] Sincronização recebida via WebSocket:', data);
        cb.onLiveSyncSync?.(data);
        return;

      case 'poll_sync':
        if (dev) console.log('[POLL] Enquete recebida via WebSocket:', data);
        cb.onPollSync?.(data);
        return;

      case 'lab_sync':
        if (dev) console.log('[LAB] Sync recebida via WebSocket:', data);
        cb.onLabSync?.(data);
        return;

      case 'member_joined':
        if (dev) console.log('[WebSocket] Novo membro na mesa:', data.member);
        cb.onMemberJoined?.(data.member);
        return;

      case 'character_stats_update':
        if (dev) console.log('[WebSocket] Stats de personagem atualizados:', data);
        cb.onCharacterStatsUpdate?.(data);
        return;

      case 'character_created':
        if (dev) console.log('[WebSocket] Novo personagem na mesa:', data.character);
        cb.onCharacterCreated?.(data.character);
        return;

      case 'message_updated':
        if (dev) console.log('[WebSocket] Mensagem atualizada:', data);
        setMessages((prev) => prev.map(msg =>
          msg.id === data.id ? { ...msg, content: data.content, editedAt: data.editedAt } : msg
        ));
        return;

      case 'message_deleted':
        if (dev) console.log('[WebSocket] Mensagem excluída:', data);
        setMessages((prev) => prev.filter(msg => msg.id !== data.id));
        return;
    }

    // Fallback: mensagem de chat comum (sem type conhecido). Permitir
    // mensagens só com mídia (sem conteúdo).
    if (!data.id || (!data.content && !data.mediaFile && !data.mediaFileId) || !data.userId) {
      return;
    }

    // Filtro por missão ativa: quando o chat está carregando só uma missão, uma
    // mensagem de outra missão (corrida na troca) não deve entrar. Sem filtro
    // (mesa sem missão), tudo passa — comportamento atual (I1).
    const missionFilter = getMissionFilter();
    if (missionFilter && data.missionId && data.missionId !== missionFilter) {
      return;
    }

    const messageData: Message = {
      id: data.id,
      content: data.content || '',
      userId: data.userId,
      userFullName: data.userFullName || '',
      messageType: data.messageType || 'dialogue',
      timestamp: data.timestamp || new Date().toISOString(),
      tableId: data.tableId || null,
      isPrivate: data.isPrivate || false,
      characterId: data.characterId,
      characterName: data.characterName,
      mediaFileId: data.mediaFileId || data.media_file_id,
      mediaFile: data.mediaFile || (data.media_file_id && data.media_file ? {
        id: data.media_file.id || data.media_file_id,
        url: data.media_file.url || data.media_file.file_url,
        filename: data.media_file.filename,
        type: data.media_file.type || data.media_file.file_type,
        size: data.media_file.size || data.media_file.file_size
      } : undefined),
      missionId: data.missionId
    };

    // Adicionar nova mensagem (evitar duplicatas e substituir a otimística)
    setMessages((prevMessages) => {
      const existsById = prevMessages.some(msg => msg.id && msg.id === messageData.id);
      if (existsById) {
        if (dev) console.log('[WebSocket] Mensagem já existe por ID:', messageData.id);
        return prevMessages;
      }

      // Procurar mensagem otimística similar (id temp-, mesmo conteúdo/mídia, recente)
      const now = Date.now();
      const optimisticIndex = prevMessages.findIndex(msg => {
        if (!msg.id || !msg.id.startsWith('temp-')) return false;
        const msgTime = new Date(msg.timestamp).getTime();
        if (now - msgTime > 30000) return false;
        const sameContent = msg.content && messageData.content && msg.content === messageData.content;
        const sameMedia = msg.mediaFileId && messageData.mediaFileId && msg.mediaFileId === messageData.mediaFileId;
        return sameContent || sameMedia;
      });

      if (optimisticIndex !== -1) {
        if (dev) console.log('[WebSocket] Substituindo mensagem otimística:', prevMessages[optimisticIndex].id, '->', messageData.id);
        const updatedMessages = [...prevMessages];
        const optimisticMsg = updatedMessages[optimisticIndex];
        // Preservar mediaFile/characterName da otimística se o backend não os enviar
        if (!messageData.mediaFile && optimisticMsg.mediaFile) {
          messageData.mediaFile = optimisticMsg.mediaFile;
        }
        if (!messageData.characterName && optimisticMsg.characterName) {
          messageData.characterName = optimisticMsg.characterName;
        }
        updatedMessages[optimisticIndex] = messageData;
        return updatedMessages;
      }

      return [...prevMessages, messageData];
    });

    cb.onNewMessage?.(messageData);
  }, []);

  const MAX_RECONNECT_ATTEMPTS = 5;
  const RECONNECT_INTERVAL = 3000;

  // Função para estabelecer a conexão WebSocket
  const connect = () => {
    if (!tableId || !token) {
      setConnectionError('ID da mesa ou token de autenticação ausente');
      // Sem isso o "Reconectando" (setado por handleManualReconnect) ficava
      // travado para sempre quando faltava tableId/token.
      setIsReconnecting(false);
      console.error('WebSocket: tableId ou token ausente', { tableId, token: token ? 'presente' : 'ausente' });
      return;
    }

    // Fechar conexão anterior se existir. close(1000) = fechamento
    // intencional: sem o código, o onclose do socket velho caía no branch
    // de reconexão automática e criava uma conexão fantasma concorrente.
    if (socketRef.current) {
      socketRef.current.close(1000, 'Nova conexão');
    }

    try {
      // Remover qualquer prefixo Bearer do token
      const cleanToken = token.replace(/^Bearer\s+/, '');

      // Construir URL do WebSocket usando a configuração base
      const socketUrl = `${WS_BASE_URL}/ws/tables/${tableId}?token=${cleanToken}`;

      // Criar nova conexão WebSocket
      const socket = new WebSocket(socketUrl);
      socketRef.current = socket;
      setWebsocket(socket);

      // Adicionar um timeout para detectar falhas de conexão rapidamente
      const connectionTimeout = setTimeout(() => {
        if (socketRef.current && socketRef.current.readyState !== WebSocket.OPEN) {
          if (import.meta.env.DEV) {
            console.log('Timeout de conexão WebSocket');
          }
          socket.close();
          setConnectionError('Tempo limite de conexão excedido');
        }
      }, 10000); // 10 segundos de timeout

      // Manipuladores de eventos do WebSocket
      socket.onopen = () => {
        clearTimeout(connectionTimeout); // Limpar o timeout
        setIsConnected(true);
        setIsReconnecting(false);
        setConnectionError(null);
        reconnectAttempts.current = 0;
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Entregar aos inscritos (presença/privado/turnos) ANTES do dispatch
          // por tipo — o parse acima é o único da cadeia. Cada callback é
          // isolado num try/catch pra um inscrito com erro não derrubar os
          // demais nem o processamento abaixo.
          subscribersRef.current.forEach((fn) => {
            try {
              fn(data);
            } catch (err) {
              console.error('[WebSocket] Erro em subscriber:', err);
            }
          });

          // Dispatch por tipo (tabela extraída para handleParsedMessage).
          handleParsedMessage(data);
        } catch (error) {
          console.error('Erro ao processar mensagem WebSocket:', error);
        }
      };

      socket.onclose = (event) => {
        if (import.meta.env.DEV) {
          console.log(`Conexão WebSocket fechada: ${event.code} - ${event.reason || 'Sem razão fornecida'}`);
        }
        clearTimeout(connectionTimeout); // Limpar o timeout
        setIsConnected(false);

        // Mostrar mensagens específicas baseadas no código de erro
        if (event.code === 1000) {
          if (import.meta.env.DEV) {
            console.log('Conexão fechada normalmente');
          }
          setConnectionError(null);
        } else if (event.code === 1001) {
          setConnectionError('Endpoint está indo embora (servidor reiniciando)');
        } else if (event.code === 1002) {
          setConnectionError('Erro de protocolo');
        } else if (event.code === 1003) {
          setConnectionError('Tipo de dados não suportado');
        } else if (event.code === 1005) {
          setConnectionError('Código de status não foi fornecido');
        } else if (event.code === 1006) {
          setConnectionError('Conexão fechada anormalmente (possível timeout)');
        } else if (event.code === 1007) {
          setConnectionError('Dados inconsistentes');
        } else if (event.code === 1008) {
          setConnectionError('Erro de autenticação. Verifique seu login.');
        } else if (event.code === 1009) {
          setConnectionError('Mensagem muito grande');
        } else if (event.code === 1010) {
          setConnectionError('Extensão não negociada');
        } else if (event.code === 1011) {
          setConnectionError('Erro interno no servidor');
        } else if (event.code === 1012) {
          setConnectionError('Reinicialização do serviço');
        } else if (event.code === 1013) {
          setConnectionError('Servidor sobrecarregado');
        } else if (event.code === 1014) {
          setConnectionError('Timeout no gateway');
        } else if (event.code === 1015) {
          setConnectionError('Falha TLS');
        } else {
          setConnectionError(`Conexão fechada com código: ${event.code}`);
        }

        // Tentar reconexão se não foi fechada propositalmente
        if (event.code !== 1000) {
          if (reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
            if (import.meta.env.DEV) {
              console.log(`Tentando reconectar em ${RECONNECT_INTERVAL / 1000} segundos...`);
            }
            // Reconexão automática em andamento: alimenta o indicador "Reconectando"
            setIsReconnecting(true);
            reconnectTimeoutRef.current = window.setTimeout(() => {
              reconnectAttempts.current += 1;
              connect();
            }, RECONNECT_INTERVAL);
          } else {
            // Esgotou as tentativas: para de mostrar "reconectando" e expõe o
            // erro acionável (o GameSession mostra um banner com "Reconectar").
            setIsReconnecting(false);
            setConnectionError('Falha ao conectar após várias tentativas');
          }
        }
      };

      socket.onerror = (error) => {
        console.error('Erro na conexão WebSocket:', error);
        clearTimeout(connectionTimeout); // Limpar o timeout

        // Teste de conexão direta para distinguir entre diferentes problemas
        const testConnection = async () => {
          try {
            // Verificar se o servidor HTTP está respondendo usando a URL da API configurada
            const httpResponse = await fetch(`${API_BASE_URL}/health`, {
              method: 'GET',
              headers: { 'Accept': 'application/json' }
            });

            if (httpResponse.ok) {
              if (import.meta.env.DEV) {
                console.log('Servidor HTTP está acessível, problema específico com WebSocket');
                console.log('WebSocket URL tentada:', `${WS_BASE_URL}/ws/tables/${tableId}`);
              }
              setConnectionError('Falha na conexão WebSocket. Verifique os logs do navegador.');
            } else {
              if (import.meta.env.DEV) {
                console.log('Servidor HTTP respondeu com erro:', httpResponse.status);
              }
              setConnectionError(`Servidor respondeu com erro: ${httpResponse.status}`);
            }
          } catch (fetchError) {
            console.error('Servidor não está acessível via HTTP:', fetchError);
            setConnectionError('Servidor inacessível. Verifique se o backend está rodando.');
          }
        };

        testConnection();
      };

    } catch (error) {
      console.error('Erro ao criar conexão WebSocket:', error);
      // Fornecer mais detalhes sobre o erro
      if (error instanceof Error) {
        setConnectionError(`Falha ao estabelecer conexão: ${error.message}`);
      } else {
        setConnectionError('Falha ao estabelecer conexão');
      }
    }
  };

  // Iniciar conexão quando o hook é montado
  useEffect(() => {
    // Logs apenas em desenvolvimento
    if (import.meta.env.DEV) {
      console.log('[WebSocket] useEffect disparado', {
        tableId,
        token: token ? 'presente' : 'ausente',
        API_BASE_URL,
        WS_BASE_URL
      });
    }

    if (tableId && token) {
      // Carregar histórico de mensagens primeiro
      loadMessageHistory().then(() => {
        // Depois de carregar o histórico, conectar ao WebSocket

        // Antes de tentar conectar, vamos garantir que o backend está respondendo
        if (import.meta.env.DEV) {
          console.log(`[WebSocket] Testando backend: ${API_BASE_URL}/health`);
        }
        fetch(`${API_BASE_URL}/health`)
          .then(response => {
            if (import.meta.env.DEV) {
              console.log('[WebSocket] Backend respondeu:', response.status, response.ok);
            }
            return response.json();
          })
          .then(() => {
            // Se o backend estiver rodando, conectar
            connect();
          })
          .catch(error => {
            console.error('[WebSocket] Backend não está respondendo:', error);
            setConnectionError('Servidor não está respondendo. Verifique se o backend está rodando.');
          });
      });
    }

    // Limpar conexão quando o componente desmontar. close(1000) evita que
    // o onclose agende reconexão depois do unmount (socket órfão).
    return () => {
      if (socketRef.current) {
        socketRef.current.close(1000, 'Componente desmontado');
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [tableId, token]);

  // Handler para fechar WebSocket quando o usuário sair da página
  // Isso garante que a lista de presença seja atualizada para outros usuários
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Fechar WebSocket de forma síncrona antes da página descarregar
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.close(1000, 'Page unload');
      }
    };

    const handlePageHide = () => {
      // Fallback para mobile/Safari que nem sempre dispara beforeunload
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.close(1000, 'Page hide');
      }
    };

    // Adicionar listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, []);

  // Função para enviar mensagem via WebSocket ou API REST
  const sendMessage = (content: string, messageType: string, characterId?: string, mediaFileId?: string, mediaFile?: { id: string; url: string; filename: string; type: string; size: number }, characterName?: string): boolean => {
    if (!content.trim() && !mediaFileId) {
      return false;
    }

    // Se estiver conectado ao WebSocket
    if (isConnected && socketRef.current) {
      try {
        const messageData = {
          content: content || '',
          message_type: messageType,
          character_id: characterId,
          media_file_id: mediaFileId,
          is_private: false,
        };

        socketRef.current.send(JSON.stringify(messageData));

        // Adicionar mensagem otimisticamente ao estado local
        const optimisticMessage: Message = {
          id: `temp-${Date.now()}-${Math.random()}`, // ID temporário
          content: content || '',
          userId: userId || '',
          userFullName: 'Você', // Será substituído quando a mensagem real chegar
          tableId,
          messageType,
          isPrivate: false,
          timestamp: new Date().toISOString(),
          characterId: characterId, // Incluir characterId na mensagem otimista
          characterName: characterName, // Incluir characterName na mensagem otimista para exibir nome do NPC corretamente
          mediaFileId: mediaFileId,
          mediaFile: mediaFile || undefined
        };

        setMessages(prev => [...prev, optimisticMessage]);

        return true;
      } catch (error) {
        console.error('Erro ao enviar mensagem WebSocket:', error);
        // Fallback para API REST se falhar
      }
    }

    // Fallback: enviar via API REST usando a rota correta
    fetch(`${API_BASE_URL}/tables/${tableId}/messages`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        content: content || '',
        message_type: messageType,
        table_id: tableId,
        character_id: characterId,
        media_file_id: mediaFileId,
        is_private: false
      })
    })
      .then(response => {
        if (response.ok) {
          return response.json();
        }
        throw new Error(`Erro ${response.status}`);
      })
      .then(data => {
        // Log apenas se for mensagem com mídia e em desenvolvimento
        if (import.meta.env.DEV && (data.mediaFileId || data.mediaFile)) {
          console.log('[MÍDIA] Mensagem enviada via API REST:', data)
        }
        setMessages(prev => [...prev, data]);
        return true;
      })
      .catch(error => {
        // O envio via REST e assincrono e a UI ja limpou o input (retorno
        // true otimista abaixo); se falhar, avisar o usuario com toast em
        // vez de engolir o erro silenciosamente.
        console.error('Erro ao enviar mensagem via API:', error);
        toast.error('Erro ao enviar mensagem. Verifique a conexão.');
        return false;
      });

    return true; // Retornar verdadeiro para não bloquear a UI
  };

  // Função para atualizar mensagem (com atualização otimística)
  const updateMessage = useCallback(async (messageId: string, content: string): Promise<void> => {
    const trimmedContent = content.trim();

    if (!trimmedContent) {
      throw new Error('A mensagem não pode estar vazia');
    }

    // Salvar estado anterior para possível rollback
    const previousMessages = messages;

    // Atualização otimística: atualizar localmente imediatamente
    setMessages(prevMessages => {
      return prevMessages.map(msg => {
        if (msg.id === messageId) {
          return {
            ...msg,
            content: trimmedContent,
            editedAt: new Date().toISOString()
          };
        }
        return msg;
      });
    });

    try {
      // Fazer a requisição para o backend
      await apiService.updateMessage(messageId, trimmedContent);
      // O WebSocket vai atualizar depois com os dados do servidor, mas a atualização otimística já mostra o resultado
    } catch (error: any) {
      // Reverter atualização otimística em caso de erro
      setMessages(previousMessages);
      throw error; // Re-lançar o erro para o componente tratar
    }
  }, [messages]);

  // Função para rolar dados via API
  const rollDice = useCallback(async (
    diceType: DiceType,
    characterId?: string,
    sessionId?: string,
    attributeId?: string,
    dice?: DiceGroup[],
    attributeIds?: string[]
  ): Promise<DiceRollResult | null> => {
    try {
      // attribute_id transforma a rolagem em TESTE: o servidor le o atributo na
      // ficha e devolve o veredito. Sem ele, a rolagem crua de sempre.
      //
      // `dice` e a BANDEJA (fase 3 do modulo de dados): varios dados numa
      // jogada so. Quando vem, o servidor ignora `dice_type` e resolve pela
      // regra da mesa. Ausente = o caminho de sempre, um dado.
      const request = {
        dice_type: diceType,
        table_id: tableId,
        character_id: characterId,
        session_id: sessionId,
        ...(attributeId ? { attribute_id: attributeId } : {}),
        ...(dice ? { dice } : {}),
        ...(attributeIds?.length ? { attribute_ids: attributeIds } : {})
      };

      if (import.meta.env.DEV) {
        console.log('🎲 Rolando dado:', request);
      }
      const result = await diceService.rollDice(request);
      if (import.meta.env.DEV) {
        console.log('🎲 Resultado recebido:', result);
      }

      return result;
    } catch (error: any) {
      console.error('Erro ao rolar dado:', error);
      // O erro já é tratado no diceService, apenas retorna null
      return null;
    }
  }, [tableId]);

  // Limpar recursos quando o componente é desmontado
  useEffect(() => {
    return () => {
      if (socketRef.current) {
        socketRef.current.close(1000, 'Componente desmontado');
      }

      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  // Reconexão manual (botão "Reconectar"): zera o contador de tentativas,
  // limpa o erro e tenta conectar de novo imediatamente.
  const handleManualReconnect = () => {
    reconnectAttempts.current = 0;
    setConnectionError(null);
    setIsReconnecting(true);
    connect();
  };

  return {
    messages,
    isConnected,
    isReconnecting,
    connectionError,
    sendMessage,
    updateMessage,
    rollDice,
    reconnect: handleManualReconnect,
    loadingHistory,
    loadMoreMessages,
    reloadHistory,
    loadingMoreMessages,
    hasMoreMessages,
    websocket: websocket, // Retornar estado em vez de Ref para garantir re-renders
    subscribe
  };
}